/**
 * 图片加载队列 - 基于 ConcurrentQueue 实现
 * - 并发控制（最大 3 个）
 * - LRU 缓存（最大 200 个）
 * - 请求合并
 * - 支持取消
 * - 超时机制（防止 pending 导致堆积）
 * - 引用计数机制（防止 ObjectURL 内存泄漏）
 */

import { ConcurrentQueue } from './ConcurrentQueue';

const MAX_CONCURRENT_REQUESTS = 3;
const REQUEST_TIMEOUT_MS = 5000; // 单个请求超时时间 5 秒
// LRU 缓存配置
const MAX_CACHE_SIZE = 200;

interface CacheEntry {
  objectUrl: string;
  refCount: number;
}

const cache = new Map<string, CacheEntry>();

/**
 * 增加引用计数
 * 组件使用图片时调用
 */
export function retainImage(url: string): void {
  const entry = cache.get(url);
  if (entry) {
    entry.refCount++;
  }
}

/**
 * 减少引用计数
 * 组件卸载或不再使用图片时调用
 * 当引用计数归零且被 LRU 淘汰时，释放 ObjectURL
 */
export function releaseImage(url: string): void {
  const entry = cache.get(url);
  if (entry && entry.refCount > 0) {
    entry.refCount--;
  }
}

/**
 * LRU 淘汰策略
 * 只淘汰引用计数为 0 的条目
 */
function evictCache(): void {
  if (cache.size < MAX_CACHE_SIZE) {
    return;
  }

  // 找到第一个引用计数为 0 的条目进行淘汰
  for (const [key, entry] of cache.entries()) {
    if (entry.refCount === 0) {
      URL.revokeObjectURL(entry.objectUrl);
      cache.delete(key);
      return;
    }
  }

  // 如果所有条目都在使用中，允许缓存暂时超出限制
  // 这是合理的，因为这些图片确实在被使用
}

/**
 * 带超时的 fetch 请求
 * 超时后自动 abort，释放并发槽位
 */
function fetchWithTimeout(url: string, signal: AbortSignal, timeoutMs: number): Promise<Response> {
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    // 监听外部 signal 取消
    const onExternalAbort = () => {
      cleanup();
      controller.abort();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    signal.addEventListener('abort', onExternalAbort, { once: true });

    // 设置超时
    timeoutId = setTimeout(() => {
      timeoutId = null;
      signal.removeEventListener('abort', onExternalAbort);
      controller.abort();
      reject(new Error(`Image load timeout after ${timeoutMs}ms`));
    }, timeoutMs);

    fetch(url, { signal: controller.signal })
      .then((res) => {
        cleanup();
        signal.removeEventListener('abort', onExternalAbort);
        resolve(res);
      })
      .catch((err) => {
        cleanup();
        signal.removeEventListener('abort', onExternalAbort);
        reject(err);
      });
  });
}

// 图片加载队列实例
const imageQueue = new ConcurrentQueue<string, string>({
  concurrency: MAX_CONCURRENT_REQUESTS,
  executor: (url, signal) => {
    return fetchWithTimeout(url, signal, REQUEST_TIMEOUT_MS)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load image: ${res.status}`);
        }
        return res.blob();
      })
      .then((blob) => URL.createObjectURL(blob));
  },
  onSuccess: (url, objectUrl) => {
    evictCache();
    // 初始引用计数为 0，等待组件调用 retainImage
    cache.set(url, { objectUrl, refCount: 0 });
  },
});

/**
 * 加载图片（带缓存和队列控制）
 * @param url 图片 URL
 * @param priority 优先级，'high' 会插入队首
 * @returns Promise<string> ObjectURL
 */
export function queueLoadImage(
  url: string,
  priority: 'high' | 'normal' = 'normal',
): Promise<string> {
  const entry = cache.get(url);
  if (entry) {
    // 缓存命中，更新 LRU 顺序（移到末尾）
    cache.delete(url);
    cache.set(url, entry);
    return Promise.resolve(entry.objectUrl);
  }
  return imageQueue.enqueue(url, priority);
}

/**
 * 取消图片加载
 * @param url 图片 URL
 */
export function cancelLoad(url: string): void {
  imageQueue.cancel(url);
}

/**
 * 检查图片是否已缓存
 * @param url 图片 URL
 */
export function isImageCached(url: string): boolean {
  return cache.has(url);
}

/**
 * 获取缓存的图片 URL（如果存在）
 * @param url 原始图片 URL
 */
export function getCachedImage(url: string): string | undefined {
  return cache.get(url)?.objectUrl;
}

/**
 * 清理所有未被引用的缓存
 * 可在内存紧张时手动调用
 */
export function cleanupUnusedCache(): void {
  const keysToDelete: string[] = [];

  cache.forEach((entry, key) => {
    if (entry.refCount === 0) {
      URL.revokeObjectURL(entry.objectUrl);
      keysToDelete.push(key);
    }
  });

  keysToDelete.forEach((key) => cache.delete(key));
}

/**
 * 获取缓存统计信息（用于调试）
 */
export function getCacheStats(): { total: number; inUse: number; unused: number } {
  let inUse = 0;
  let unused = 0;

  cache.forEach((entry) => {
    if (entry.refCount > 0) {
      inUse++;
    } else {
      unused++;
    }
  });

  return { total: cache.size, inUse, unused };
}
